import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { Site, Area } from '../types'
import PolygonEditor from './PolygonEditor'

interface AssetFormProps {
  assetTypes: Record<string, any>
  onSubmit: () => void
  onCancel: () => void
  initialData?: any
  isEditing?: boolean
  assetId?: number
}

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Location picker component for point positioning
function LocationPicker({ position, onLocationChange, center }: { 
  position: [number, number] | null, 
  onLocationChange: (coords: [number, number]) => void,
  center?: [number, number] | null
}) {
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng
        onLocationChange([lng, lat]) // Store as [lng, lat] to match polygon format
      }
    })
    return null
  }

  // Convert position from [lng, lat] to [lat, lng] for map center
  const mapCenter = position ? [position[1], position[0]] as [number, number] : center || [59.3293, 18.0686]

  return (
    <MapContainer
      center={mapCenter}
      zoom={13}
      style={{ height: '300px', width: '100%' }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <MapClickHandler />
      {position && <Marker position={[position[1], position[0]]} />} {/* Convert [lng, lat] to [lat, lng] */}
    </MapContainer>
  )
}

function AssetForm({ assetTypes, onSubmit, onCancel, initialData, isEditing = false, assetId }: AssetFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    site_id: '',
    area_id: '',
    comment: '',
    time_started: '',
    time_ended: '',
    position_type: 'point',
    position_coords: null,
    ...initialData
  })
  
  const [sites, setSites] = useState<Site[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showLocationMap, setShowLocationMap] = useState(false)

  useEffect(() => {
    fetchSites()
    
    // Set default position type based on asset type
    if (formData.type && assetTypes[formData.type]) {
      const defaultPositionType = assetTypes[formData.type].defaultPositionType
      if (!formData.position_type || formData.position_type !== defaultPositionType) {
        setFormData((prev: any) => ({ 
          ...prev, 
          position_type: defaultPositionType,
          position_coords: null // Reset coordinates when changing type
        }))
      }
    }
  }, [formData.type, assetTypes])

  useEffect(() => {
    if (formData.site_id) {
      fetchAreas(parseInt(formData.site_id))
    } else {
      setAreas([])
      setFormData((prev: any) => ({ ...prev, area_id: '' }))
    }
  }, [formData.site_id])

  // Set default time_started to current date/time for new assets
  useEffect(() => {
    if (!isEditing && !formData.time_started) {
      const now = new Date()
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
      setFormData((prev: any) => ({ 
        ...prev, 
        time_started: now.toISOString().slice(0, 16)
      }))
    }
  }, [isEditing])

  // Calculate map center based on selected area or site
  const calculateMapCenter = (): [number, number] | null => {
    // If area is selected and has coordinates, use area center
    if (formData.area_id) {
      const selectedArea = areas.find(area => area.id === parseInt(formData.area_id))
      if (selectedArea && selectedArea.polygon_coords) {
        const coords = selectedArea.polygon_coords
        const centerLat = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length
        const centerLng = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length
        return [centerLat, centerLng]
      }
    }
    
    // If site is selected and has coordinates, use site center
    if (formData.site_id) {
      const selectedSite = sites.find(site => site.id === parseInt(formData.site_id))
      if (selectedSite && selectedSite.polygon_coords) {
        const coords = selectedSite.polygon_coords
        const centerLat = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length
        const centerLng = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length
        return [centerLat, centerLng]
      }
    }
    
    return null // No center available, use default
  }

  const fetchSites = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/sites')
      if (response.ok) {
        const data = await response.json()
        setSites(data)
      }
    } catch (err) {
      console.error('Failed to fetch sites:', err)
    }
  }

  const fetchAreas = async (siteId: number) => {
    try {
      const response = await fetchWithAuth(`/api/admin/sites/${siteId}/areas`)
      if (response.ok) {
        const data = await response.json()
        setAreas(data)
      }
    } catch (err) {
      console.error('Failed to fetch areas:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (!formData.name || !formData.name.trim()) {
      setError('Asset name is required')
      setLoading(false)
      return
    }

    if (!formData.type) {
      setError('Asset type is required')
      setLoading(false)
      return
    }

    if (!formData.site_id) {
      setError('Site is required')
      setLoading(false)
      return
    }

    if (!formData.position_coords) {
      setError('Position is required - please set a location on the map')
      setLoading(false)
      return
    }

    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description?.trim() || null,
        site_id: parseInt(formData.site_id),
        area_id: formData.area_id ? parseInt(formData.area_id) : null,
        comment: formData.comment?.trim() || null,
        time_started: formData.time_started || null,
        time_ended: formData.time_ended || null,
        position_type: formData.position_type,
        position_coords: formData.position_coords
      }

      const url = isEditing ? `/api/assets/${assetId}` : '/api/assets'
      const method = isEditing ? 'PUT' : 'POST'
      
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} asset`)
      }

      onSubmit()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditing ? 'update' : 'create'} asset`)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePositionChange = (coords: [number, number] | [number, number][]) => {
    setFormData((prev: any) => ({ ...prev, position_coords: coords }))
  }

  const getAssetTypeInfo = (type: string) => {
    return assetTypes[type] || { name: type, icon: '📦', defaultPositionType: 'point' }
  }

  return (
    <div className="task-form-overlay">
      <div className="task-form-container compact">
        <div className="task-form-header">
          <h3>📦 {isEditing ? 'Edit Asset' : 'Create New Asset'}</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} className="task-form">
          <div className="form-group">
            <label>Asset Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter asset name..."
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Asset Type *</label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                required
              >
                <option value="">Select type...</option>
                {Object.entries(assetTypes).map(([key, type]) => (
                  <option key={key} value={key}>
                    {type.icon} {type.name}
                  </option>
                ))}
              </select>
            </div>

            {formData.type && (
              <div className="form-group">
                <label>Position Type</label>
                <select
                  value={formData.position_type}
                  onChange={(e) => {
                    handleChange('position_type', e.target.value)
                    setFormData((prev: any) => ({ ...prev, position_coords: null })) // Reset coordinates
                  }}
                >
                  <option value="point">📍 Point Location</option>
                  <option value="polygon">🟦 Area/Polygon</option>
                </select>
                <small style={{ fontSize: '0.8rem', color: '#666' }}>
                  Recommended: {getAssetTypeInfo(formData.type).defaultPositionType === 'point' ? '📍 Point' : '🟦 Area'}
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe the asset..."
              rows={2}
            />
          </div>

          <div className="form-section">
            <h4>📍 Location</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Site *</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleChange('site_id', e.target.value)}
                  required
                >
                  <option value="">Select site...</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Area (Optional)</label>
                <select
                  value={formData.area_id}
                  onChange={(e) => handleChange('area_id', e.target.value)}
                  disabled={!formData.site_id}
                >
                  <option value="">Select area...</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <div className="location-header">
                <label>Position *</label>
                <button 
                  type="button" 
                  className="btn btn-sm"
                  onClick={() => setShowLocationMap(!showLocationMap)}
                >
                  {showLocationMap ? 'Hide Map' : `Set ${formData.position_type === 'point' ? 'Point' : 'Area'} on Map`}
                </button>
              </div>
              
              {formData.position_coords && (
                <div className="location-display">
                  {formData.position_type === 'point' ? (
                    <>📍 Lat: {(formData.position_coords as [number, number])[1].toFixed(6)}, Lng: {(formData.position_coords as [number, number])[0].toFixed(6)}</>
                  ) : (
                    <>🟦 Polygon with {(formData.position_coords as [number, number][]).length} points</>
                  )}
                  <button 
                    type="button" 
                    className="clear-location" 
                    onClick={() => setFormData((prev: any) => ({ ...prev, position_coords: null }))}
                  >
                    ×
                  </button>
                </div>
              )}

              {showLocationMap && (
                <div className="map-section">
                  {formData.position_type === 'point' ? (
                    <LocationPicker
                      position={formData.position_coords as [number, number]}
                      onLocationChange={handlePositionChange}
                      center={calculateMapCenter()}
                    />
                  ) : (
                    <PolygonEditor
                      initialPolygon={formData.position_coords as [number, number][]}
                      onPolygonChange={(coordinates: number[][] | null) => coordinates && handlePositionChange(coordinates as [number, number][])}
                      onClose={() => setShowLocationMap(false)}
                      title="Set Asset Area"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h4>⏱️ Timing</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Started</label>
                <input
                  type="datetime-local"
                  value={formData.time_started}
                  onChange={(e) => handleChange('time_started', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Ended (Optional)</label>
                <input
                  type="datetime-local"
                  value={formData.time_ended}
                  onChange={(e) => handleChange('time_ended', e.target.value)}
                />
                <small style={{ fontSize: '0.8rem', color: '#666' }}>
                  Leave empty for active assets
                </small>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Comment</label>
            <textarea
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              placeholder="Additional notes about this asset..."
              rows={2}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? '✓ Update Asset' : '✓ Create Asset')}
            </button>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetForm